import { Checkbox } from "@rio-cloud/rio-uikit";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import UptimeAssetRow from "./UptimeAssetRow";
import { Asset } from "../../../models/asset";

import "../styles.scss";

const LIMIT = 100;

interface UptimeTagRowProps {
	tagId: string;
	tagName: string;
	isChecked: boolean;
	assets: Asset[];
	selectedAssetIds: Set<string>;
	onTagSelect: (event: ChangeEvent<HTMLInputElement>) => void;
	isAssetChecked: (assetId: string) => boolean;
	onAssetSelect: (assetId: string) => void;
	searchQuery: string;
	viewMode: string;
}

const UptimeTagRow = ({
	tagId,
	tagName,
	isChecked,
	assets,
	selectedAssetIds,
	onTagSelect,
	isAssetChecked,
	onAssetSelect,
	searchQuery,
	viewMode,
}: UptimeTagRowProps) => {
	const [page, setPage] = useState(1);
	
	const filteredAssets = useMemo(() => {
		if (!searchQuery || viewMode !== "assets") return assets;
		const lowerQuery = searchQuery.toLowerCase();
		return assets.filter(
			(asset) =>
				asset.chassis?.toLowerCase().includes(lowerQuery) ||
				asset.name?.toLowerCase().includes(lowerQuery) ||
				asset.licensePlate?.toLowerCase().includes(lowerQuery),
		);
	}, [assets, searchQuery, viewMode]);

	const filteredSelectedCount = useMemo(
		() => filteredAssets.filter((asset) => selectedAssetIds.has(asset.id)).length,
		[filteredAssets, selectedAssetIds],
	);
	
	const [isExpanded, setIsExpanded] = useState(searchQuery && filteredAssets.length > 0 ? true : false);

	// Reset page when search query or view mode changes
	useEffect(() => {
		setPage(1);
	}, [searchQuery, viewMode]);

	useEffect(() => {
		setIsExpanded(searchQuery && filteredAssets.length > 0 ? true : false);
	}, [searchQuery]);

	const visibleAssets = useMemo(
		() => filteredAssets.slice(0, page * LIMIT),
		[filteredAssets, page],
	);
	const hasMore = visibleAssets.length < filteredAssets.length;

	return filteredAssets.length > 0 ? (
		<div
			key={tagId}
			className={`TreeNodeContainer user-select-none ${isExpanded ? "open" : ""}`}
		>
			<div className="TreeNode from-group" onClick={() => setIsExpanded(!isExpanded)}>
				<Checkbox
					className="TreeCheckbox"
					checked={isChecked || (filteredAssets.length > 0 && filteredSelectedCount === filteredAssets.length)}
					indeterminate={filteredSelectedCount > 0 && filteredSelectedCount < filteredAssets.length}
					onChange={(e) => {
						e.stopPropagation();
						onTagSelect(e);
					}}
				/>
				<div className="TreeLabel">
					<div className="TreeLabelName">
						<div className="TreeLabelNameText display-flex">
							<div className="TreeLabelNameTextHeadline">{tagName}</div>
							<div className="TreeLabelCount label margin-left-10 margin-right-10 label-muted label-filled label-condensed">{filteredAssets.length}</div>
						</div>
						<div
							className={`margin-inline-10 rioglyph text-size-12 ${isExpanded ? "rioglyph-chevron-up" : "rioglyph-chevron-down"}`}
						/>
					</div>
				</div>
			</div>

			{isExpanded && visibleAssets.map((asset) => <UptimeAssetRow
					key={asset.id}
					asset={asset}
					tagName={tagName}
					isTagSelected={isChecked}
					isChecked={isAssetChecked(asset.id)}
					onAssetSelect={onAssetSelect}
				/>)}

			{isExpanded && hasMore && (
				<div
					className="LoadMore cursor-pointer text-center py-8 text-color-primary"
					onClick={() => setPage(prev => prev + 1)}
				>Carregar mais</div>
			)}
		</div>
	) : null;
};

export default UptimeTagRow;
